// Pay planner maths. Pure: no React, no fetch, so it can be unit tested on its own.
// Money is cents (kept fractional until display), time is hours, weight is grams,
// pace is grams per hour.

export type PlanDay = { day: string; hours: number; grams: number };
export type PlanCrew = { id: number; name: string; hourlyCents: number | null; days: PlanDay[] };

/** Every pay scheme is one formula, applied per day worked:
 *
 *    pay = max(floor·h, base·h + rate·max(0, g − target·h))
 *
 *  Hourly is rate 0. Piece rate is base 0, target 0. Base + bonus uses all three:
 *  the bonus is only paid on grams above the target pace. `floorCents` is a
 *  guaranteed minimum per hour, topped up day by day. */
export type Scheme = { baseCents: number; rateCentsPerG: number; targetGph: number; floorCents: number };

export type PersonResult = {
  id: number; name: string; hours: number; grams: number;
  pay: number; perHour: number; topUp: number; floorDays: number;
};
export type SchemeResult = {
  people: PersonResult[]; hours: number; grams: number; cost: number; costPerGram: number;
  /** Top-up paid to bring slow days up to the guaranteed minimum. */
  topUp: number;
  /** What the same scheme costs if worked out on each person's totals instead of day by day.
   * The formula is convex (max of linear terms), so day by day always costs at least this:
   * the gap is the price of slow days that averages hide. */
  averagedCost: number;
};

function payFor(s: Scheme, hours: number, grams: number) {
  const earned = s.baseCents * hours + s.rateCentsPerG * Math.max(0, grams - s.targetGph * hours);
  const floor = s.floorCents * hours;
  return { pay: Math.max(floor, earned), topUp: Math.max(0, floor - earned) };
}

/** `uplift`: fractional pace change assumed when pay depends on output (rate > 0),
 * e.g. 0.1 = trimmers go 10% faster on the same hours. Hours are held fixed. */
export function simulate(crew: PlanCrew[], scheme: Scheme | ((c: PlanCrew) => Scheme), uplift = 0): SchemeResult {
  let averagedCost = 0;
  const people = crew.map((c) => {
    const s = typeof scheme === "function" ? scheme(c) : scheme;
    const k = s.rateCentsPerG > 0 ? 1 + uplift : 1;
    let hours = 0, grams = 0, pay = 0, topUp = 0, floorDays = 0;
    for (const d of c.days) {
      const r = payFor(s, d.hours, d.grams * k);
      hours += d.hours; grams += d.grams * k; pay += r.pay; topUp += r.topUp;
      if (r.topUp > 0) floorDays++;
    }
    averagedCost += payFor(s, hours, grams).pay;
    return { id: c.id, name: c.name, hours, grams, pay, perHour: hours ? pay / hours : 0, topUp, floorDays };
  });
  const sum = (f: (p: PersonResult) => number) => people.reduce((t, p) => t + f(p), 0);
  const grams = sum((p) => p.grams);
  const cost = sum((p) => p.pay);
  return { people, hours: sum((p) => p.hours), grams, cost, costPerGram: grams ? cost / grams : 0, topUp: sum((p) => p.topUp), averagedCost };
}

/** What the crew costs today: each person's own hourly rate plus the org piece rate. */
export function simulateToday(crew: PlanCrew[], pieceCentsPerG: number | null): SchemeResult {
  return simulate(crew, (c) => ({ baseCents: c.hourlyCents ?? 0, rateCentsPerG: pieceCentsPerG ?? 0, targetGph: 0, floorCents: 0 }));
}

export function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export type Goal = (r: SchemeResult) => boolean;
export const goals = {
  /** Same total spend as `ref` for the same hours. */
  sameCost: (ref: SchemeResult): Goal => (r) => r.cost >= ref.cost,
  /** Every person earns at least what they earn under `ref` (half-cent tolerance). */
  nobodyWorseOff: (ref: SchemeResult): Goal => (r) => r.people.every((p, i) => p.pay >= ref.people[i].pay - 0.5),
  /** The middle trimmer takes home at least `cents` per hour. */
  medianHourly: (cents: number): Goal => (r) => median(r.people.map((p) => p.perHour)) >= cents,
};

/** Lowest rate (cents/g) in `scheme` that meets `goal`, or null if none does.
 * Bisection works because pay never falls as the rate rises, so each goal flips
 * from false to true exactly once. */
export function solveRate(crew: PlanCrew[], scheme: Scheme, uplift: number, goal: Goal): number | null {
  const meets = (rate: number) => goal(simulate(crew, { ...scheme, rateCentsPerG: rate }, uplift));
  if (meets(0)) return 0;
  let lo = 0, hi = 0.01;
  while (!meets(hi)) {
    lo = hi; hi *= 2;
    if (hi > 1e6) return null;
  }
  for (let i = 0; i < 50; i++) {
    const mid = (lo + hi) / 2;
    if (meets(mid)) hi = mid; else lo = mid;
  }
  return hi;
}

/** Pace at which hourly pay and a piece rate cost the same per gram. Slower trimmers
 * are cheaper on piece rate, faster ones are cheaper on hourly. */
export function breakEvenGph(wageCents: number, rateCentsPerG: number): number | null {
  return rateCentsPerG > 0 ? wageCents / rateCentsPerG : null;
}

/** Smallest pace lift (0..1 in 0.5% steps) at which `scheme` costs no more per gram than
 * `targetCostPerGram`. Null if it never gets there. A scan, not bisection: cost per gram
 * is not monotone in pace once a target threshold is involved. */
export function upliftToMatch(crew: PlanCrew[], scheme: Scheme, targetCostPerGram: number): number | null {
  if (scheme.rateCentsPerG <= 0) return null;
  for (let u = 0; u <= 1.0001; u += 0.005) {
    if (simulate(crew, scheme, u).costPerGram <= targetCostPerGram + 1e-9) return u;
  }
  return null;
}

/** Average pace and how much it swings day to day (coefficient of variation of daily pace). */
export function paceStats(c: PlanCrew): { gph: number; spread: number } {
  const hours = c.days.reduce((t, d) => t + d.hours, 0);
  const gph = hours ? c.days.reduce((t, d) => t + d.grams, 0) / hours : 0;
  const daily = c.days.filter((d) => d.hours > 0).map((d) => d.grams / d.hours);
  if (daily.length < 2 || !gph) return { gph, spread: 0 };
  const mean = daily.reduce((t, x) => t + x, 0) / daily.length;
  const sd = Math.sqrt(daily.reduce((t, x) => t + (x - mean) ** 2, 0) / (daily.length - 1));
  return { gph, spread: mean ? sd / mean : 0 };
}
